import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import QRCode from 'qrcode';
import { 
  ArrowLeft, 
  Download, 
  MessageSquare, 
  Calendar, 
  User, 
  Package,
  Clock,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  Building,
  ChevronRight
} from 'lucide-react';
import { API_URL } from '../../Config';

// Composant Toast
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 4000);

    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-emerald-500' : 
                  type === 'error' ? 'bg-rose-500' : 
                  'bg-blue-500';

  return (
    <div className={`fixed top-6 right-6 z-50 px-4 py-3 ${bgColor} text-white rounded-lg shadow-lg transform transition-transform duration-300 animate-slide-in`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          {type === 'success' && (
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
          {type === 'error' && (
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
          <span className="font-medium">{message}</span>
        </div>
        <button 
          onClick={onClose}
          className="ml-4 text-white hover:text-gray-200 focus:outline-none"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Template PDF caché
const PdfTemplate = ({ demande, historique }) => {
  const qrRef = useRef();

  useEffect(() => {
    if (demande) {
      QRCode.toDataURL(window.location.href, { width: 200, margin: 2 })
        .then(url => {
          if (qrRef.current) qrRef.current.src = url;
        })
        .catch(console.error);
    }
  }, [demande]);

  if (!demande) return null;

  // Fonction de formatage de date sécurisée pour le PDF
  const safeFormatDate = (dateString) => {
    if (!dateString) return 'Date inconnue';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        // Essayer parseISO si la date directe échoue
        const parsed = parseISO(dateString);
        if (isValid(parsed)) {
          return format(parsed, 'dd MMMM yyyy à HH:mm', { locale: fr });
        }
        return 'Date invalide';
      }
      return format(date, 'dd MMMM yyyy à HH:mm', { locale: fr });
    } catch {
      return 'Date invalide';
    }
  };

  const getStatusText = (statut) => {
    if (!statut) return '';
    return statut.replace(/_/g, ' ').toUpperCase();
  };

  const formatHistoriqueDate = (dateString) => {
    if (!dateString) return 'Date non disponible';
    return safeFormatDate(dateString);
  };
return (
  <div style={{ 
    padding: '30px', 
    fontFamily: 'Helvetica, Arial, sans-serif', 
    background: 'white', 
    width: '210mm', 
    minHeight: '297mm',
    boxSizing: 'border-box'
  }}>
    
    {/* En-tête avec QR à gauche et numéro à droite */}
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'flex-start',
      marginBottom: '25px',
      paddingBottom: '15px',
      borderBottom: '2px solid black'
    }}>
      {/* QR Code à gauche */}
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        <div>
          <img ref={qrRef} alt="QR Code" style={{ 
            width: '80px', 
            height: '80px',
            border: '1px solid #e5e7eb'
          }} />
          <div style={{ 
            fontSize: '9px', 
            color: '#6b7280', 
            marginTop: '5px',
            maxWidth: '80px'
          }}>
          </div>
        </div>
      </div>

      {/* Titre et informations au centre */}
      <div style={{ textAlign: 'center', flex: 1 }}>
        <h1 style={{ 
          fontSize: '24px', 
          fontWeight: 'bold', 
          color: 'black',
          marginBottom: '5px'
        }}>
          Historique de la demande
        </h1>
      </div>

      {/* Numéro de commande à droite */}
      <div style={{ 
        textAlign: 'right',
        background: '#f8fafc',
        padding: '10px',
        borderRadius: '8px',
        border: '2px solid black',
        minWidth: '120px'
      }}>
        <div style={{ 
          fontSize: '11px', 
          color: '#64748b',
          marginBottom: '5px'
        }}>
        </div>
        <div style={{ 
          fontSize: '24px', 
          fontWeight: 'bold', 
          color: 'black'
        }}>
          #{demande.numero || demande.id || 'N/A'}
        </div>
      </div>
    </div>

    {/* Statistiques rapides */}
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-between',
      marginBottom: '25px',
      padding: '15px',
      background: '#f8fafc',
      borderRadius: '8px',
      border: '1px solid #e2e8f0'
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '20px', fontWeight: 'bold', color: 'black' }}>
          {historique?.length || 0}
        </div>
        <div style={{ fontSize: '11px', color: '#64748b' }}>Événements</div>
      </div>
      
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#000000' }}>
          {historique?.filter(e => e.commentaire).length || 0}
        </div>
        <div style={{ fontSize: '11px', color: '#64748b' }}>Commentaires</div>
      </div>
      
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#000000' }}>
          {new Set(historique?.map(e => e.user?.nom).filter(Boolean)).size || 0}
        </div>
        <div style={{ fontSize: '11px', color: '#64748b' }}>Utilisateurs</div>
      </div>
    </div>

    {/* Tableau d'historique */}
    <h2 style={{ 
      fontSize: '16px', 
      fontWeight: 'bold', 
      marginBottom: '15px', 
      color: '#1f2937',
      paddingBottom: '8px',
      borderBottom: '2px solid #e5e7eb'
    }}>
      Chronologie des événements
    </h2>

    {historique?.length > 0 ? (
      <div style={{ marginBottom: '25px' }}>
        {/* En-tête du tableau */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '40px 120px 100px 1fr 120px',
          background: '#f3f4f6',
          border: '1px solid #d1d5db',
          fontSize: '11px',
          fontWeight: '600',
          color: '#000000'
        }}>
          <div style={{ padding: '10px', borderRight: '1px solid #d1d5db' }}>#</div>
          <div style={{ padding: '10px', borderRight: '1px solid #d1d5db' }}>Date</div>
          <div style={{ padding: '10px', borderRight: '1px solid #d1d5db' }}>Utilisateur</div>
          <div style={{ padding: '10px', borderRight: '1px solid #d1d5db' }}>Événement</div>
          <div style={{ padding: '10px' }}>Commentaire</div>
        </div>

        {/* Corps du tableau */}
        {historique.map((entry, i) => {
          const entryDate = formatHistoriqueDate(entry?.created_at);
          const actionType = entry.type_action?.replace(/_/g, ' ') || 'Action';
          
          return (
            <div 
              key={i}
              style={{ 
                display: 'grid', 
                gridTemplateColumns: '40px 120px 100px 1fr 120px',
                border: '1px solid #d1d5db',
                borderTop: 'none',
                fontSize: '10px',
                color: '#4b5563'
              }}
            >
              {/* Numéro */}
              <div style={{ 
                padding: '8px', 
                borderRight: '1px solid #d1d5db',
                background: i % 2 === 0 ? '#ffffff' : '#f9fafb',
                textAlign: 'center',
                fontWeight: '600'
              }}>
                {i + 1}
              </div>

              {/* Date */}
              <div style={{ 
                padding: '8px', 
                borderRight: '1px solid #d1d5db',
                background: i % 2 === 0 ? '#ffffff' : '#f9fafb'
              }}>
                <div>{entryDate}</div>
              </div>

              {/* Utilisateur */}
              <div style={{ 
                padding: '8px', 
                borderRight: '1px solid #d1d5db',
                background: i % 2 === 0 ? '#ffffff' : '#f9fafb'
              }}>
                <div style={{ fontWeight: '600' }}>{entry.user?.nom || 'Système'}</div>
                <div style={{ fontSize: '9px', color: '#6b7280' }}>
                  {entry.user?.role || 'Utilisateur'}
                </div>
              </div>

              {/* Événement */}
              <div style={{ 
                padding: '8px', 
                borderRight: '1px solid #d1d5db',
                background: i % 2 === 0 ? '#ffffff' : '#f9fafb'
              }}>
                <div style={{ 
                  display: 'inline-block', 
                  padding: '2px 6px', 
                  color: 'black',
                  borderRadius: '3px',
                  fontSize: '9px',
                  fontWeight: '600',
                  marginBottom: '4px'
                }}>
                  {actionType}
                </div>
                <div style={{ lineHeight: '1.3' }}>
                  {entry.description || 'Aucune description'}
                </div>
                {entry.materiel && (
                  <div style={{ 
                    fontSize: '9px', 
                    color: '#191919',
                    marginTop: '3px'
                  }}>
                    📦 {entry.materiel.nom}
                  </div>
                )}
              </div>

              {/* Commentaire */}
              <div style={{ 
                padding: '8px',
                background: i % 2 === 0 ? '#ffffff' : '#f9fafb',
                fontStyle: entry.commentaire ? 'normal' : 'italic'
              }}>
                {entry.commentaire || '—'}
              </div>
            </div>
          );
        })}
      </div>
    ) : (
      <div style={{ 
        textAlign: 'center', 
        padding: '30px', 
        color: '#6b7280',
        fontStyle: 'italic',
        border: '1px dashed #d1d5db',
        borderRadius: '6px',
        marginBottom: '25px'
      }}>
        Aucun événement enregistré
      </div>
    )}
  </div>
);
};

export default function HistoriqueDemande() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [demande, setDemande] = useState(null);
  const [historique, setHistorique] = useState([]);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const pdfRef = useRef();

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ ...toast, show: false });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/historique/demande/${id}`, { headers });
        console.log('Données API reçues:', {
          demande: res.data.data.demande,
          historique: res.data.data.historique
        });
        setDemande(res.data.data.demande || null);
        setHistorique(res.data.data.historique?.data || []);
      } catch (err) {
        console.error('Erreur lors du chargement:', err);
        showToast('Accès refusé ou demande introuvable', 'error');
        setTimeout(() => navigate('/mes-demandes', { replace: true }), 2000);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  const addComment = async () => {
    if (!comment.trim()) {
      showToast('Veuillez saisir un commentaire', 'error');
      return;
    }
    
    try {
      await axios.post(
        `${API_URL}/historique/demande/${id}/comment`,
        { commentaire: comment },
        { headers }
      );
      setComment('');
      showToast('Commentaire ajouté avec succès');
      const res = await axios.get(`${API_URL}/historique/demande/${id}`, { headers });
      setHistorique(res.data.data.historique?.data || []);
    } catch (error) {
      console.error('Erreur:', error);
      showToast('Erreur lors de l\'ajout du commentaire', 'error');
    }
  };

  const exportToPDF = async () => {
    if (!pdfRef.current || !demande) {
      showToast('Données non disponibles pour l\'export', 'error');
      return;
    }

    try {
      setExporting(true);
      const element = pdfRef.current;
      const canvas = await html2canvas(element, { 
        scale: 2, 
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false
      });
      
      const imgData = canvas.toDataURL('image/png', 1.0);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const width = pdf.internal.pageSize.getWidth();
      const height = (canvas.height * width) / canvas.width;

      pdf.addImage(imgData, 'PNG', 5, 5, width - 10, height - 10);
      pdf.save(`Historique_Demande_${demande.numero || demande.id || id}.pdf`);
      showToast('PDF exporté avec succès');
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      showToast('Erreur lors de la génération du PDF', 'error');
    } finally {
      setExporting(false);
    }
  };

  const getActionColor = (type) => {
    if (!type) return 'bg-gray-100 text-gray-800';
    
    const colors = {
      creation_demande: 'bg-blue-100 text-blue-800',
      validation: 'bg-emerald-100 text-emerald-800',
      refus: 'bg-rose-100 text-rose-800',
      preparation: 'bg-amber-100 text-amber-800',
      sortie_stock: 'bg-purple-100 text-purple-800',
      recuperation: 'bg-emerald-100 text-emerald-800',
      commentaire: 'bg-gray-100 text-gray-800',
      modification_quantite: 'bg-amber-100 text-amber-800',
      annulation: 'bg-gray-200 text-gray-900',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return 'Date inconnue';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        // Essayer parseISO si la date directe échoue
        const parsed = parseISO(dateString);
        if (isValid(parsed)) {
          return format(parsed, 'dd MMMM yyyy à HH:mm', { locale: fr });
        }
        return 'Date invalide';
      }
      return format(date, 'dd MMMM yyyy à HH:mm', { locale: fr });
    } catch {
      return 'Date invalide';
    }
  };

  const getStatusInfo = (statut) => {
    const statusMap = {
      en_attente: {
        label: 'En attente',
        color: 'bg-amber-100 text-amber-800',
        icon: Clock
      },
      validee: {
        label: 'Validée',
        color: 'bg-emerald-100 text-emerald-800',
        icon: CheckCircle
      },
      refusee: {
        label: 'Refusée',
        color: 'bg-rose-100 text-rose-800',
        icon: XCircle
      },
      annulee: {
        label: 'Annulée',
        color: 'bg-gray-100 text-gray-800',
        icon: AlertCircle
      },
      pret_recuperation: {
        label: 'Prêt à récupérer',
        color: 'bg-blue-100 text-blue-800',
        icon: Package
      },
      en_preparation: {
        label: 'En préparation',
        color: 'bg-indigo-100 text-indigo-800',
        icon: Package
      },
    };
    return statusMap[statut] || {
      label: statut?.replace(/_/g, ' ') || 'Inconnu',
      color: 'bg-gray-100 text-gray-800',
      icon: AlertCircle
    };
  };

  const getRoleBadge = (role) => {
    if (!role) return 'bg-gray-100 text-gray-800';
    
    const roles = {
      admin: 'bg-red-100 text-red-800',
      responsable: 'bg-purple-100 text-purple-800',
      technicien: 'bg-blue-100 text-blue-800',
      utilisateur: 'bg-green-100 text-green-800',
      magasinier: 'bg-amber-100 text-amber-800',
    };
    return roles[role] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement de l'historique...</p>
        </div>
      </div>
    );
  }

  if (!demande) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl border p-8 text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-4">Demande introuvable</h2>
          <p className="text-gray-500 mb-6">La demande que vous recherchez n'existe pas ou a été supprimée.</p>
          <Link 
            to="/mes-demandes" 
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour aux demandes
          </Link>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo(demande.statut);
  const StatusIcon = statusInfo.icon;

  return (
    <>
      {/* Toast Notification */}
      {toast.show && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={hideToast}
        />
      )}

      {/* PDF caché */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0, width: '210mm' }}>
        <div ref={pdfRef}>
          <PdfTemplate demande={demande} historique={historique} />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <Link 
              to="/mes-demandes" 
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour aux demandes
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">Historique de la demande</h1>
          </div>
          <button
            onClick={exportToPDF}
            disabled={exporting || !demande}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            {exporting ? 'Génération...' : 'Exporter en PDF'}
          </button>
        </div>

        {/* Informations de la demande */}
        <div className="bg-white rounded-xl border p-6 mb-6">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <div className="text-xl font-bold text-gray-900">
                    #{demande.numero || demande.id}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${statusInfo.color}`}>
                <StatusIcon className="w-4 h-4" />
                {statusInfo.label}
              </span>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Ajouter un commentaire */}
        <div className="bg-white rounded-xl border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-blue-600" />
            Ajouter un commentaire
          </h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addComment()}
              placeholder="Écrivez un commentaire visible par tous..."
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              onClick={addComment}
              disabled={!comment.trim()}
              className="px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Envoyer
            </button>
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Chronologie des événements</h2>
            <p className="text-sm text-gray-500 mt-1">
              {historique.length} événement{historique.length > 1 ? 's' : ''} enregistré{historique.length > 1 ? 's' : ''}
            </p>
          </div>

          {historique.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Aucun historique disponible</p>
            </div>
          ) : (
            <div className="divide-y">
              {historique.map((entry, index) => {
                const date = entry?.created_at ? formatDisplayDate(entry.created_at).split('à')[0].trim() : 'Date inconnue';
                const time = entry?.created_at ? formatDisplayDate(entry.created_at).split('à')[1]?.trim() : '';
                const actionColor = getActionColor(entry.type_action);
                
                return (
                  <div key={entry.id || index} className="px-6 py-4">
                    <div className="flex flex-col lg:flex-row gap-4">
                      {/* Date et heure */}
                      <div className="lg:w-32">
                        <div className="text-sm font-medium text-gray-900">{date}</div>
                        {time && <div className="text-xs text-gray-500">{time}</div>}
                        <div className="mt-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${getRoleBadge(entry.user?.role)}`}>
                            {entry.user?.role || 'Utilisateur'}
                          </span>
                        </div>
                      </div>

                      {/* Ligne de timeline */}
                      <div className="flex flex-col items-center lg:w-12">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${actionColor}`}>
                          {index + 1}
                        </div>
                        {index < historique.length - 1 && (
                          <div className="w-0.5 h-8 bg-gray-300 mt-2 flex-1" />
                        )}
                      </div>

                      {/* Contenu */}
                      <div className="flex-1">
                        <div className="mb-2">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs px-3 py-1 rounded-full ${actionColor}`}>
                              {entry.type_action?.replace(/_/g, ' ') || 'Action'}
                            </span>
                            {entry.materiel && (
                              <span className="text-sm text-blue-600 font-medium">
                                {entry.materiel.nom}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600 flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <span className="font-medium">{entry.user?.nom || 'Système'}</span>
                            <span className="text-gray-400">•</span>
                            <span className="text-gray-500">{entry.user?.email || ''}</span>
                          </div>
                        </div>

                        <p className="text-gray-700 text-sm mb-3">
                          {entry.description || 'Aucune description disponible'}
                        </p>

                        {entry.commentaire && (
                          <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                            <div className="flex items-start gap-2">
                              <MessageSquare className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                              <div>
                                <div className="text-sm text-blue-800 italic">"{entry.commentaire}"</div>
                                <div className="text-xs text-blue-600 mt-1">
                                  Commentaire ajouté
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
}